import type {SupportMessages} from "../i18n/en/support";

/**
 * Pure support-request validation (kitecorp/transferify-api-service#101, epic #218 "logic outside
 * React"): the client-side mirror of every rejection `POST /support/request` can raise, so the user is
 * stopped AT THE FIELD they can fix instead of by one opaque failure on which a retry can never
 * succeed — the same lesson ui#326 taught the public feedback form.
 *
 * The verdict carries error KEYS, never translated copy — {@link supportFieldErrorText} resolves them
 * against a catalog the caller supplies, so this module holds no i18n concern and no React import.
 */

/**
 * A key into the `support.fieldErrors` catalog namespace. Derived from the English catalog so that
 * renaming or dropping a message is a compile error here rather than a silent runtime `undefined`.
 */
export type SupportFieldErrorKey = keyof SupportMessages["support"]["fieldErrors"];

/**
 * Longest subject the api will accept, mirroring `SupportRequest.subject` `@Size(max = 200)`
 * (kitecorp/transferify-api-service#101).
 *
 * Counted in UTF-16 code units, exactly as Java's `String.length()` counts them, so the two agree on a
 * Romanian diacritic (1 unit each side) and on an emoji (2 units each side).
 * `supportRequestValidation.test.ts` pins the number as a literal on this side so the two cannot drift
 * silently.
 */
export const MAX_SUPPORT_SUBJECT_LENGTH = 200;

/**
 * Longest message body the api will accept, mirroring `SupportRequest.message` `@Size(max = 2000)`.
 * Four times the public feedback note's bound, because this form collects real problem reports rather
 * than a one-line remark. Pinned as a literal in the test for the same anti-drift reason as above.
 */
export const MAX_SUPPORT_MESSAGE_LENGTH = 2000;

/** The draft fields the rules read, in the shape `SupportRequestApi.send` posts them. */
export interface SupportRequestDraft {
	subject: string;
	message: string;
}

/**
 * The verdict, per FIELD rather than as a single first-failure: a user whose subject is malformed AND
 * whose message is too long must be able to fix both in one pass instead of discovering the second
 * problem only after resubmitting.
 */
export interface SupportFieldErrors {
	subject: SupportFieldErrorKey | null;
	message: SupportFieldErrorKey | null;
}

/** A draft with nothing wrong with it — also the reset value for a freshly opened dialog. */
export const NO_SUPPORT_FIELD_ERRORS: SupportFieldErrors = {subject: null, message: null};

/**
 * Whether `value` contains an ISO control character, matching Java's `Character.isISOControl` exactly:
 * U+0000–U+001F (C0, which includes tab, newline and carriage return) and U+007F–U+009F (DEL plus C1).
 *
 * This is a TRANSPORT constraint, not a taste one: a control character in a MIME `Subject:` header
 * either breaks the header or lets it be split, so the SMTP layer refuses it and the api enforces that
 * at its boundary. Mirroring it here is what turns an unreadable 400 into a fixable field error.
 */
export function containsIsoControlCharacter(value: string): boolean {
	for (const character of value) {
		const code = character.codePointAt(0)!;
		if (code <= 0x1f || (code >= 0x7f && code <= 0x9f)) {
			return true;
		}
	}
	return false;
}

/**
 * Validates a support-request draft against every rejection the api can raise.
 *
 * `maxMessageLength` exists because the posted body is not always the typed one: choosing a topic
 * prepends an inbox tag (`core/support/feedbackTopic.ts`), and the api's `@Size` measures what it
 * RECEIVES. The caller passes the budget that is actually left, so the user is stopped at the CHARACTER
 * length they really have instead of meeting a 400 no retry could clear. Defaulted, so every existing
 * caller and its tests keep the plain 2000-character rule.
 *
 * Characters only. `SupportRequest.message` also carries `@MaxUtf8Bytes(max = 2000)`, which this module
 * has never mirrored, so a message inside 2000 characters but over 2000 UTF-8 bytes is still refused by
 * the api rather than at the field — pre-existing, and untouched by the ASCII tags above. See the note
 * on `maxMessageLengthForTopic`.
 *
 * @example
 * validateSupportRequestDraft({subject: "", message: "Help"});
 * // => {subject: "emptySubject", message: null}
 */
export function validateSupportRequestDraft(
	draft: SupportRequestDraft,
	maxMessageLength: number = MAX_SUPPORT_MESSAGE_LENGTH,
): SupportFieldErrors {
	return {
		subject: subjectErrorOf(draft.subject),
		message: messageErrorOf(draft.message, maxMessageLength),
	};
}

/**
 * Whether both required fields hold text: what arms the dialog's Send button.
 *
 * Complete is not VALID. A draft can be complete and still be refused by
 * {@link validateSupportRequestDraft} (a control character in the subject), and that refusal belongs
 * at the field, where it can name the fix, rather than in a button that stays off without a reason.
 *
 * It also says nothing about a send in flight (kitecorp/transferify-ui#3076). The view DISABLES the
 * button for an incomplete draft, and a disabled button drops the keyboard focus it holds out of the
 * dialog, so the in-flight state is reported separately and the button stays focusable through it.
 */
export function isSupportDraftComplete(draft: SupportRequestDraft): boolean {
	return !isBlank(draft.subject) && !isBlank(draft.message);
}

/** Whether a verdict blocks the send. */
export function hasSupportFieldError(errors: SupportFieldErrors): boolean {
	return errors.subject !== null || errors.message !== null;
}

/**
 * Resolves one field-error key against the catalog's `support.fieldErrors` namespace.
 *
 * A function rather than a bare `fieldErrors[key]` index because the namespace mixes plain strings
 * with `(max) => string` builders: indexing it generically types as `string | ((max) => string)` and,
 * through an untyped text slot, renders the BUILDER instead of the message. Resolving here also keeps
 * the api's bounds out of the copy, so the number can only ever come from the constants above.
 */
export function supportFieldErrorText(
	fieldErrors: SupportMessages["support"]["fieldErrors"],
	key: SupportFieldErrorKey,
	maxMessageLength: number = MAX_SUPPORT_MESSAGE_LENGTH,
): string {
	switch (key) {
		case "emptySubject":
			return fieldErrors.emptySubject;
		case "subjectTooLong":
			return fieldErrors.subjectTooLong(MAX_SUPPORT_SUBJECT_LENGTH);
		case "subjectControlCharacters":
			return fieldErrors.subjectControlCharacters;
		case "emptyMessage":
			return fieldErrors.emptyMessage;
		case "messageTooLong":
			// The budget the user actually has, which a chosen topic shrinks — quoting the raw api bound
			// here would name a limit the field itself refuses to reach.
			return fieldErrors.messageTooLong(maxMessageLength);
	}
}

/** `@NotBlank`, then `@Size(max = 200)`, then the control-character rule — in the api's own order. */
function subjectErrorOf(subject: string): SupportFieldErrorKey | null {
	if (isBlank(subject)) {
		return "emptySubject";
	}
	// The RAW length, because `@Size` measures the raw string the request carries, not a trimmed one.
	if (subject.length > MAX_SUPPORT_SUBJECT_LENGTH) {
		return "subjectTooLong";
	}
	if (containsIsoControlCharacter(subject)) {
		return "subjectControlCharacters";
	}
	return null;
}

/**
 * `@NotBlank` then `@Size(max = 2000)`. Deliberately NO control-character rule: the message rides in
 * the MIME body, where line breaks are ordinary text, and refusing them would make the form useless
 * for the multi-paragraph problem reports it exists to collect.
 */
function messageErrorOf(message: string, maxMessageLength: number): SupportFieldErrorKey | null {
	if (isBlank(message)) {
		return "emptyMessage";
	}
	if (message.length > maxMessageLength) {
		return "messageTooLong";
	}
	return null;
}

/** The api's `@NotBlank`: nothing but whitespace counts as empty. */
function isBlank(value: string): boolean {
	return value.trim().length === 0;
}
